import { cva } from 'class-variance-authority';
import type {
  ClassProp,
  ClassValue,
  StringToBoolean,
} from 'class-variance-authority/types';

import { customTwMerge } from '@/app/utils/custom-tw-merge';

type ConfigSchema = Record<string, Record<string, ClassValue>>;
type ConfigVariants<T extends ConfigSchema> = {
  [Variant in keyof T]?: StringToBoolean<keyof T[Variant]> | null | undefined;
};
type ConfigVariantsMulti<T extends ConfigSchema> = {
  [Variant in keyof T]?:
    | StringToBoolean<keyof T[Variant]>
    | StringToBoolean<keyof T[Variant]>[]
    | undefined;
};
type Config<T> = T extends ConfigSchema
  ? {
      variants?: T;
      defaultVariants?: ConfigVariants<T>;
      compoundVariants?: (T extends ConfigSchema
        ? (ConfigVariants<T> | ConfigVariantsMulti<T>) & ClassProp
        : ClassProp)[];
    }
  : never;

type Props<T> = T extends ConfigSchema
  ? ConfigVariants<T> & ClassProp
  : ClassProp;

export function cnva<T>(base?: ClassValue, config?: Config<T>) {
  const cvaClass = cva(base, config);

  return (params?: Props<T>) => {
    const generatedClasses = cvaClass(params);
    return customTwMerge(generatedClasses);
  };
}
